import { z } from 'zod';

/**
 * Stable, machine-readable error codes. Clients branch on `code`, never on `message`.
 */
export const errorCodeSchema = z.enum([
  'VALIDATION_FAILED',
  'BAD_REQUEST',
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  /** Signed in with a password; the second factor is still missing. */
  'MFA_REQUIRED',
  'FORBIDDEN',
  /** Platform staff must enroll a second factor before using the platform surface. */
  'MFA_ENROLLMENT_REQUIRED',
  'TENANT_SUSPENDED',
  'NOT_FOUND',
  'METHOD_NOT_ALLOWED',
  'CONFLICT',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'INTERNAL',
  'SERVICE_UNAVAILABLE',
  /** The store exists but is not open to the public yet (provisioning, or copy still being written). */
  'STORE_NOT_OPEN',
  /** The store's owner has closed the site for now (store settings). */
  'STORE_CLOSED',
  /** The store takes no orders right now (store settings); the catalog stays browsable. */
  'ORDERING_CLOSED',
  /** A cart line cannot be ordered as it is (unavailable, not enough stock, price changed). */
  'CART_INVALID',
]);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

/** Each code maps to exactly one HTTP status. */
export const ERROR_HTTP_STATUS: Readonly<Record<ErrorCode, number>> = {
  VALIDATION_FAILED: 400,
  BAD_REQUEST: 400,
  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  MFA_REQUIRED: 401,
  FORBIDDEN: 403,
  MFA_ENROLLMENT_REQUIRED: 403,
  TENANT_SUSPENDED: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_MEDIA_TYPE: 415,
  RATE_LIMITED: 429,
  INTERNAL: 500,
  SERVICE_UNAVAILABLE: 503,
  STORE_NOT_OPEN: 503,
  STORE_CLOSED: 503,
  ORDERING_CLOSED: 409,
  CART_INVALID: 409,
};

export const errorDetailSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type ErrorDetail = z.infer<typeof errorDetailSchema>;

/** The single error envelope returned by every API endpoint. */
export const errorResponseSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    requestId: z.string(),
    details: z.array(errorDetailSchema).optional(),
  }),
});
export type ErrorResponse = z.infer<typeof errorResponseSchema>;
